import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAmplitude } from './AmplitudeProvider';
import { selectors as variantsSelectors } from './variantsSlice';

import type { RootState } from './variantsSlice';

export function useFeatureFlag(
    flagName: string,
    defaultValue = false,
    variantName = 'on',
) {
    const { experiment } = useAmplitude();
    const hasFetched = useSelector(variantsSelectors.selectHasFetched);
    const storedVariant = useSelector((state: RootState) =>
        variantsSelectors.selectById(state, flagName),
    );

    // Resolve the flag synchronously during render so SSR (and the hydration
    // render) produce the correct output. On non-cached pages, the server
    // dispatches `variantsReceived` before rendering, so `hasFetched` is true
    // and the variant is already in the store.
    const [isEnabled, setIsEnabled] = useState(() =>
        (hasFetched ? storedVariant?.value === variantName : defaultValue),
    );
    const [isLoading, setIsLoading] = useState(() => !hasFetched);

    useEffect(() => {
        if (!experiment || !hasFetched) {
            return;
        }

        const flag = experiment.variant(flagName);

        setIsLoading(false);

        // Fire exposure for any real assignment (treatment or control),
        // but not for fallback/unassigned users per Amplitude docs.
        // `flag.key` is set for any real assignment (treatment or control).
        // The SDK returns `{}` (no key) for unassigned/fallback users.
        // Control groups may have `key` without `value`, so we check `key`.
        if (flag?.key) {
            experiment.exposure(flagName);
        }

        setIsEnabled(flag?.value === variantName);
        // defaultValue is intentionally not in the dep array as it's being used by useState(defaultValue)
    }, [experiment, flagName, hasFetched, variantName]);

    return { isEnabled, isLoading };
}
